import { Component, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo, gql } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash';

import { HeaderComponent } from '../header/header.component';
import { NavComponent } from '../navigation/nav.component';
import { AuthStore } from '@store/auth.store';
import { ServiceChargeConfig } from '@interfaces/models.interface';
import { isUnsupportedByApi } from '../../../shared/utils/graphql-support.util';
import {
  isMissingPermissionError,
  MISSING_PERMISSION_MESSAGE,
} from '@shared/utils/admin-permission-error.util';
import { AdminPermission } from '@enums/admin-permission.enum';
import { AdminPermissionService } from '@services/admin-permission.service';

const SERVICE_CHARGE_FIELDS = `
  serviceType
  serviceFeePercentage
  feeType
  flatFee
  maxServiceFee
  affiliateSharePercentage
  isActive
  updatedBy
  dateCreated
  dateUpdated
`;

const SERVICE_CHARGE_CONFIGS = gql`
  query serviceChargeConfigs {
    serviceChargeConfigs { ${SERVICE_CHARGE_FIELDS} }
  }
`;

const UPDATE_SERVICE_CHARGE_CONFIG = gql`
  mutation updateServiceChargeConfig($input: UpdateServiceChargeConfigInput!) {
    updateServiceChargeConfig(input: $input) { ${SERVICE_CHARGE_FIELDS} }
  }
`;

/** The services the backend keeps a charge config for, in display order. */
const SERVICE_LABELS: Record<string, string> = {
  express: 'OK! Express',
  okride: 'OK! Ride',
  mototaxi: 'OK! MotoTaxi',
  oktaxi: 'OK! Taxi',
};

/**
 * Service Fee configuration (CMS).
 *
 * Edit only, by design. The backend exposes exactly `serviceChargeConfigs` and
 * `updateServiceChargeConfig` — there is no create and no delete, because the
 * row set is fixed: one config per service, seeded by migration. A missing row
 * is a migration problem, not something an admin should paper over from here.
 *
 * Percentages are stored as fractions (0.10 = 10%) and edited here as percents.
 * That conversion is the whole reason this component holds a separate form
 * object rather than binding to the record: an admin typing "10" into a field
 * bound straight to `serviceFeePercentage` would set a 1000% fee.
 */
@Component({
  selector: 'app-service-fee-cms',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent, NavComponent],
  templateUrl: './service-fee-cms.component.html',
  styleUrl: './service-fee-cms.component.css',
})
export class ServiceFeeCmsComponent implements OnInit {
  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private router: Router,
    private authStore: AuthStore,
    private permissions: AdminPermissionService,
  ) {}

  /** Editing is offered only with `service_fees.manage` — the API refuses it otherwise. */
  get canManage(): boolean {
    return this.permissions.can(AdminPermission.ServiceFeesManage);
  }

  public configs: ServiceChargeConfig[] = [];
  public isLoading = false;
  public loadError: 'none' | 'unsupported' | 'failed' = 'none';

  public showDetails = false;
  public enableEdit = false;
  public isSaving = false;
  public detailViewed: ServiceChargeConfig | null = null;

  /** Percent-denominated mirror of the record being edited. */
  public form: {
    serviceFeePercentage: number | null;
    feeType: 'percentage' | 'flat';
    flatFee: number | null;
    maxServiceFee: number | null;
    affiliateSharePercentage: number | null;
    isActive: boolean;
  } = this.emptyForm();

  private emptyForm() {
    return {
      serviceFeePercentage: null as number | null,
      feeType: 'percentage' as 'percentage' | 'flat',
      flatFee: null as number | null,
      maxServiceFee: null as number | null,
      affiliateSharePercentage: null as number | null,
      isActive: true,
    };
  }

  ngOnInit(): void {
    this.fetchConfigs();
  }

  serviceLabel(serviceType: string): string {
    return SERVICE_LABELS[serviceType] ?? serviceType;
  }

  /** 0.1 -> 10. Kept to 4 decimals so 1/3-style fractions do not drift. */
  private toPercent(fraction: number | null | undefined): number | null {
    if (fraction === null || fraction === undefined) return null;
    return Math.round(fraction * 100 * 10000) / 10000;
  }

  /** 10 -> 0.1 */
  private toFraction(percent: number | null | undefined): number | null {
    if (percent === null || percent === undefined || `${percent}` === '') return null;
    return Math.round(Number(percent) * 1000000) / 100000000;
  }

  fetchConfigs(): void {
    this.isLoading = true;
    this.loadError = 'none';

    this.apollo
      .watchQuery({ fetchPolicy: 'network-only', query: SERVICE_CHARGE_CONFIGS })
      .valueChanges.subscribe({
        next: (res: any) => {
          this.isLoading = false;
          const rows: ServiceChargeConfig[] = res?.data?.['serviceChargeConfigs'] ?? [];
          const order = Object.keys(SERVICE_LABELS);
          this.configs = cloneDeep(rows).sort(
            (a, b) => order.indexOf(a.serviceType) - order.indexOf(b.serviceType),
          );

          // Keep an open detail view in step with what the server just returned.
          if (this.detailViewed) {
            const refreshed = this.configs.find(
              (config) => config.serviceType === this.detailViewed!.serviceType,
            );
            if (refreshed) this.detailViewed = refreshed;
          }
        },
        error: (error) => {
          this.isLoading = false;
          if (this.handledAuthError(error)) return;
          this.loadError = isUnsupportedByApi(error) ? 'unsupported' : 'failed';
        },
      });
  }

  viewDetails(config: ServiceChargeConfig): void {
    this.detailViewed = config;
    this.showDetails = true;
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  returnToList(): void {
    this.showDetails = false;
    this.enableEdit = false;
    this.detailViewed = null;
    this.form = this.emptyForm();
  }

  enableEditForm(): void {
    this.enableEdit = true;
    this.resetFormFromRecord();
  }

  disableEditForm(): void {
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  private resetFormFromRecord(): void {
    const record = this.detailViewed;
    if (!record) {
      this.form = this.emptyForm();
      return;
    }

    this.form = {
      serviceFeePercentage: this.toPercent(record.serviceFeePercentage),
      feeType: record.feeType ?? 'percentage',
      flatFee: record.flatFee ?? null,
      maxServiceFee: record.maxServiceFee ?? null,
      affiliateSharePercentage: this.toPercent(record.affiliateSharePercentage),
      isActive: record.isActive,
    };
  }

  /** A flat fee charges a fixed amount, so the cap and the percentage are moot. */
  get isFlatFee(): boolean {
    return this.form.feeType === 'flat';
  }

  private validationError(): string | null {
    const { serviceFeePercentage, affiliateSharePercentage, flatFee, maxServiceFee } = this.form;

    if (this.isFlatFee) {
      if (flatFee === null || `${flatFee}` === '' || Number(flatFee) < 0) {
        return 'Enter a flat fee of zero or more.';
      }
    } else {
      if (serviceFeePercentage === null || `${serviceFeePercentage}` === '') {
        return 'Enter a service fee percentage.';
      }
      if (Number(serviceFeePercentage) < 0 || Number(serviceFeePercentage) > 100) {
        return 'Service fee must be between 0% and 100%.';
      }
      if (maxServiceFee !== null && `${maxServiceFee}` !== '' && Number(maxServiceFee) < 0) {
        return 'Maximum service fee cannot be negative.';
      }
    }

    if (affiliateSharePercentage === null || `${affiliateSharePercentage}` === '') {
      return 'Enter an affiliate share percentage.';
    }
    if (Number(affiliateSharePercentage) < 0 || Number(affiliateSharePercentage) > 100) {
      return 'Affiliate share must be between 0% and 100%.';
    }

    return null;
  }

  confirmUpdate(modal: TemplateRef<any>): void {
    const error = this.validationError();
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  updateConfig(successModal: TemplateRef<any>): void {
    const record = this.detailViewed;
    const error = this.validationError();
    if (!record || error) {
      if (error) this.toastr.error(error);
      return;
    }

    this.isSaving = true;

    // serviceFeePercentage is non-null on the input even for a flat fee, so a
    // flat config keeps whatever share it had rather than being zeroed here.
    const input: Record<string, any> = {
      serviceType: record.serviceType,
      serviceFeePercentage: this.isFlatFee
        ? record.serviceFeePercentage
        : this.toFraction(this.form.serviceFeePercentage),
      feeType: this.form.feeType,
      flatFee: this.isFlatFee ? Number(this.form.flatFee) : null,
      maxServiceFee: this.isFlatFee
        ? null
        : this.form.maxServiceFee === null || `${this.form.maxServiceFee}` === ''
          ? null
          : Number(this.form.maxServiceFee),
      affiliateSharePercentage: this.toFraction(this.form.affiliateSharePercentage),
      isActive: this.form.isActive,
    };

    this.apollo
      .mutate({ mutation: UPDATE_SERVICE_CHARGE_CONFIG, variables: { input } })
      .subscribe({
        next: ({ data }: any) => {
          this.isSaving = false;
          const updated: ServiceChargeConfig | undefined = data?.['updateServiceChargeConfig'];
          if (!updated) {
            this.toastr.error('The service fee was not updated. Please try again.');
            return;
          }

          this.detailViewed = cloneDeep(updated);
          this.configs = this.configs.map((config) =>
            config.serviceType === updated.serviceType ? cloneDeep(updated) : config,
          );
          this.resetFormFromRecord();

          this.modalService.dismissAll();
          this.enableEdit = false;
          this.modalService.open(successModal, {
            backdrop: 'static',
            keyboard: false,
            size: 'lg',
          });
        },
        error: (error) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          if (this.handledAuthError(error)) return;
          this.toastr.error(error?.message || 'Error updating the service fee configuration');
        },
      });
  }

  closeAllModals(): void {
    this.modalService.dismissAll();
  }

  /** Logs out on an expired/forbidden session; returns true when it did. */
  private handledAuthError(error: any): boolean {
    // Refused by the admin's roles, not a dead session: say so and stay signed in.
    if (isMissingPermissionError(error)) {
      this.toastr.error(MISSING_PERMISSION_MESSAGE);
      return true;
    }

    const isAuthError =
      error?.message === 'Unauthorized' ||
      error?.networkError?.status === 403 ||
      error?.graphQLErrors?.[0]?.extensions?.status === 403 ||
      error?.errors?.[0]?.extensions?.status === 403;

    if (!isAuthError) return false;

    this.authStore.logout().subscribe(() => {
      this.router.navigate(['/admin/login']);
    });
    return true;
  }
}
